import { useEffect, useRef } from "react";

// Shared, ordered registry of every currently-active useEscapeKey caller.
// Each instance still installs its own single `document` keydown listener
// (no closer coupling than that) - but only fires `onEscape` when it's the
// LAST (most recently activated, i.e. topmost) entry. Without this, two
// simultaneously-active callers - e.g. a ConfirmDialog opened over a
// ModalShell, a Select dropdown open inside a ModalShell, or (since
// _app/entityModals/store.js started supporting real modal-over-modal
// stacking) two nested entity modals - each independently caught the same
// Escape press and both fired, closing the outer one along with whatever
// was actually on top of it instead of just the top.
let activeStack = [];

/**
 * Fires `onEscape` on the Escape key, only while `isActive`, and only for
 * the topmost currently-active caller (see `activeStack` above). Same
 * shape as the sibling `useClickOutside` hook: a thin, dependency-free DOM
 * listener with no domain knowledge - the caller decides what "escape"
 * means (close now, or intercept and ask first).
 */
function useEscapeKey(isActive, onEscape) {
  const idRef = useRef();
  if (!idRef.current) idRef.current = Symbol("useEscapeKey");

  useEffect(() => {
    if (!isActive) return;

    const id = idRef.current;
    activeStack = [...activeStack, id];

    function handleKeyDown(event) {
      if (event.key !== "Escape") return;
      if (activeStack[activeStack.length - 1] !== id) return;
      onEscape();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      activeStack = activeStack.filter((entryId) => entryId !== id);
    };
  }, [isActive, onEscape]);
}

export default useEscapeKey;
